import React, { useState } from "react";
import { TextInput, Popover, ActionIcon } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { Controller, useFormContext } from "react-hook-form";
import moment from "moment";
import { SlCalender } from "react-icons/sl";

const RHFCustomDatePicker = ({
  name,
  rules,
  defaultValue = "",
  minDate,
    maxDate,
                            hideError=false,
  
  disabled = false,
  maxYear = new Date().getFullYear(),
  onValueChange,
  ...props
}) => {
  const { control } = useFormContext();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      render={({ field, fieldState: { error } }) => {
        // Auto-format MM-DD-YYYY while typing
        const handleDobChange = (e) => {
          let cleaned = e.target.value.replace(/\D/g, "");
          cleaned = cleaned.slice(0, 8);

          if (cleaned.length > 4) {
            cleaned = cleaned.replace(/^(\d{2})(\d{2})(\d{0,4})/, "$1-$2-$3");
          } else if (cleaned.length > 2) {
            cleaned = cleaned.replace(/^(\d{2})(\d{0,2})/, "$1-$2");
          }

          const parts = cleaned.split("-");
          if (parts.length > 0) {
            let mm = parts[0];
            if (mm && parseInt(mm, 10) > 12) {
              cleaned = "12" + (parts[1] ? "-" + parts[1] : "") + (parts[2] ? "-" + parts[2] : "");
            }
          }
          if (parts.length > 2) {
            let yyyy = parts[2];
            const currentYear = new Date().getFullYear();
            if (yyyy && parseInt(yyyy, 10) > maxYear) {
              cleaned = parts[0] + "-" + parts[1] + "-" + currentYear;
            } else if (yyyy && String(yyyy).length === 4 && parseInt(yyyy, 10) < 1920) {
              cleaned = parts[0] + "-" + parts[1] + "-1920";
            }
          }
          if (parts.length > 1) {
            let dd = parts[1];
            if (dd && parseInt(dd, 10) > 31) {
              cleaned = parts[0] + "-31" + (parts[2] ? "-" + parts[2] : "");
            }
          }

          // Update React Hook Form state
          field.onChange(cleaned);

          // Trigger optional external callback
          if (onValueChange) {
            onValueChange(cleaned);
          }
        };

        // Convert "MM-DD-YYYY" string into a valid JS Date object for Mantine DatePicker
        const parsedDate =
          field.value &&
          field.value.length === 10 &&
          moment(field.value, "MM-DD-YYYY", true).isValid()
            ? moment(field.value, "MM-DD-YYYY").toDate()
            : null;

        return (
          <Popover
            opened={isOpen}
            onChange={setIsOpen}
            position="bottom-start"
            withArrow
            shadow="md"
            disabled={disabled}
          >
            <Popover.Target>
              <TextInput
                {...props}
                type="tel"
                variant={props.variant}
                placeholder="MM-DD-YYYY"
                value={field.value ?? ""}
                onChange={handleDobChange}
                onBlur={field.onBlur} // Hook Form blur handling
                disabled={disabled}
                error={!hideError && error?.message}
                rightSection={
                  <ActionIcon
                    variant="transparent"
                    color="gray"
                    style={{ marginRight: "15px" }}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevents input click event from firing simultaneously
                      if (!disabled) setIsOpen((o) => !o);
                    }}
                  >
                    <SlCalender size={16} />
                  </ActionIcon>
                }
                styles={{
                  label: {
                    fontSize: "16px",
                    lineHeight: "24px",
                    fontWeight: 400,
                    color: "#222222",
                    marginBottom: "8px",
                  },
                  ...props.styles, // Allow extending styles via props
                }}
              />
            </Popover.Target>

            <Popover.Dropdown p="sm">
              <DatePicker
                size="sm"
                value={parsedDate}
                onChange={(date) => {
                  if (date) {
                    const formattedDate = moment(date).format("MM-DD-YYYY");
                    field.onChange(formattedDate);

                    if (onValueChange) {
                      onValueChange(formattedDate);
                    }

                    setIsOpen(false);
                  }
                }}
                minDate={minDate}
                maxDate={maxDate}
                defaultDate={parsedDate || new Date()}
              />
            </Popover.Dropdown>
          </Popover>
        );
      }}
    />
  );
};

export default RHFCustomDatePicker;